import { useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function Checkout({ storeId, topupId, onComplete }: { storeId: number; topupId: number; onComplete: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-4">
      <PaymentElement />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <Button className="w-full" disabled={!stripe || !elements || paying} onClick={async () => {
        if (!stripe || !elements) return;
        setPaying(true); setError("");
        const result = await stripe.confirmPayment({ elements, redirect: "if_required", confirmParams: { return_url: window.location.href } });
        if (result.error) { setError(result.error.message ?? "Payment failed"); setPaying(false); return; }
        const response = await fetch(`/api/order/stores/${storeId}/sms-topups/${topupId}/confirm`, { method: "POST", headers: authHeaders() });
        const data = await response.json() as { error?: string };
        if (!response.ok) { setError(data.error ?? "Payment is processing. Refresh shortly."); setPaying(false); return; }
        onComplete();
      }}>{paying ? "Processing…" : "Pay securely by card"}</Button>
    </div>
  );
}

export function SmsTopup({ storeId, minimumPence, onComplete }: { storeId: number; minimumPence: number; onComplete: () => void }) {
  const [amount, setAmount] = useState((minimumPence / 100).toFixed(2));
  const [checkout, setCheckout] = useState<{ clientSecret: string; publishableKey: string; topupId: number } | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const amountPence = Math.round(Number(amount) * 100);
  return (
    <div className="space-y-3">
      {!checkout ? <>
        <div className="flex gap-2">
          <div className="relative flex-1"><span className="absolute left-3 top-2 text-sm text-slate-500">£</span><Input className="pl-7" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} /></div>
          <Button disabled={starting || !Number.isInteger(amountPence) || amountPence < minimumPence} onClick={async () => {
            setStarting(true); setError("");
            const response = await fetch(`/api/order/stores/${storeId}/sms-topups/payment-intent`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ amountPence, idempotencyKey: crypto.randomUUID() }) });
            const data = await response.json() as { clientSecret?: string; publishableKey?: string; topupId?: number; error?: string };
            setStarting(false);
            if (!response.ok || !data.clientSecret || !data.publishableKey || !data.topupId) { setError(data.error ?? "Could not start payment"); return; }
            setCheckout({ clientSecret: data.clientSecret, publishableKey: data.publishableKey, topupId: data.topupId });
          }}>{starting ? "Preparing…" : "Top up"}</Button>
        </div>
        <p className="text-xs text-slate-500">Minimum top-up £{(minimumPence / 100).toFixed(2)}. Prices include VAT.</p>
      </> : <Elements stripe={loadStripe(checkout.publishableKey)} options={{ clientSecret: checkout.clientSecret, appearance: { theme: "stripe" } }}>
        <Checkout storeId={storeId} topupId={checkout.topupId} onComplete={() => { setCheckout(null); onComplete(); }} />
      </Elements>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
